import { Controller } from '@hotwired/stimulus';
import CodeMirror, { EditorFromTextArea } from 'codemirror';
import 'codemirror/addon/display/autorefresh';
import 'codemirror/addon/selection/active-line';
import 'codemirror/mode/ruby/ruby';
import 'codemirror/mode/javascript/javascript';

export default class TriggersFormController extends Controller {
  static targets = [
    'changedFieldContainer',
    'cronFieldContainer',
    'bulkFieldContainer',
    'manualFieldContainer',
    'nonBulkFieldContainer',
    'statusTransitionsContainer',
    'languageSelect',
    'codeBlock',
    'when',
  ];

  private when:string;

  declare readonly changedFieldContainerTarget:HTMLElement;
  declare readonly statusTransitionsContainerTarget:HTMLElement;
  declare readonly whenTargets:HTMLElement[];
  declare readonly codeBlockTargets:HTMLElement[];
  declare readonly cronFieldContainerTargets:HTMLElement[];
  declare readonly bulkFieldContainerTargets:HTMLElement[];
  declare readonly manualFieldContainerTargets:HTMLElement[];
  declare readonly nonBulkFieldContainerTargets:HTMLElement[];
  declare readonly languageSelectTarget:HTMLInputElement;

  editorTextAreas:{ [index:string]:EditorFromTextArea } = {};

  connect() {
    this.whenTargets
      .filter((el:HTMLInputElement) => el.checked)
      .forEach((el:HTMLInputElement) => {
        this.toggleChangedField(el.value)
        this.toggleFields(el.value)
        this.when = el.value
      });
    setInterval(() => this.checkStatusTransitionsVisibility(), 100);
  }

  codeBlockTargetConnected(element:HTMLTextAreaElement) {
    let cm = this.editorTextAreas[element.name] = CodeMirror.fromTextArea(element, {
      mode: this.languageSelectTarget.value,
      tabSize: 2,
      lineNumbers: true,
      lineWrapping: true,
      autoRefresh: true,
      styleActiveLine: true,
      viewportMargin: Infinity,
      indentWithTabs: true,
    });
    if (cm.getValue().length > 0) {
      let collapsible = cm.getWrapperElement().closest('.-collapsible')
      if (collapsible) {
        collapsible.classList.remove('collapsed');
        collapsible.getElementsByTagName('div')[0].style.display = 'block';
      }
    }
  }

  addStatusTransition(event:InputEvent) {
    let lastEl = this.statusTransitionsContainerTarget.querySelector('.form--field:last-child')
    if (!lastEl) return

    let count = this.statusTransitionsContainerTarget.querySelectorAll('.form--field').length
    let newEl = lastEl.cloneNode(true) as HTMLElement;

    newEl.hidden = false
    newEl.querySelectorAll("input, select").forEach((el:HTMLInputElement) => {
      el.value = ''
      el.id = el.id.replace(/_\d+_/, `_${count}_`)
      el.name = el.name.replace(/\[\d+]/, `[${count}]`)
    })

    this.statusTransitionsContainerTarget.appendChild(newEl)
  }

  deleteStatusTransition(event:InputEvent) {
    let elem = (event.target as HTMLElement).closest('.form--field') as HTMLElement;
    (elem.querySelector("input[name$='[_destroy]']") as HTMLInputElement).value = '1';
    elem.hidden = true
  }

  updateWhen(event:InputEvent) {
    const when = (event.target as HTMLInputElement).value;
    this.toggleChangedField(when);
    this.toggleFields(when);
    this.when = when;
  }

  updateLanguage(event:InputEvent) {
    const language = (event.target as HTMLInputElement).value;
    Object.values(this.editorTextAreas).forEach((cm) => {
      cm.setOption('mode', language);
    });
  }

  toggleStatusTransition(toggle:boolean) {
    let el = this.statusTransitionsContainerTarget.closest('fieldset')
    if (el) el.hidden = !toggle;
  }

  getChangedFields():string[] {
    let changedFields = document.querySelectorAll("input[name$='[changed_fields][]']")
    let fields:string[] = []
    changedFields.forEach((el:HTMLInputElement) => fields.push(el.value))
    return fields
  }

  checkStatusTransitionsVisibility() {
    this.toggleStatusTransition(this.getChangedFields().indexOf('status_id') >= 0 && this.when != 'cron')
  }

  toggleChangedField(when:String) {
    this.changedFieldContainerTarget.hidden = !(when == 'save' || when == 'update');
  }

  toggleTargets(targets:HTMLElement[], toggle:boolean) {
    targets.forEach((el) => {
      el.hidden = !toggle
      el.querySelectorAll('input, textarea, select').forEach((el:HTMLInputElement|HTMLTextAreaElement) => el.disabled = !toggle)
    })
  }

  toggleFields(when:string) {
    this.toggleTargets(this.manualFieldContainerTargets, when == 'manual');
    this.toggleTargets(this.cronFieldContainerTargets, when == 'cron');
    this.toggleTargets(this.bulkFieldContainerTargets, when == 'cron' || when == 'manual');
    this.toggleTargets(this.nonBulkFieldContainerTargets, when != 'cron' && when != 'manual');
  }
}
